import React, { FC, Fragment, useEffect, useState } from 'react';
import { Order } from 'models/order';
import { Box, Divider } from '@mui/material';
import DeliveryActions from './delivery-actions.component';
import PickUpActions from './pick-up-actions.component';
import { TransportLine } from 'models/transport';
import { useTranslation } from 'react-i18next';
import { ApolloError, useLazyQuery, useQuery } from '@apollo/client';
import { transportLinesForOrderTemplate } from 'apollo/templates/transport/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { TransportChannel } from 'models/transport';
import TransportLinesForOrder from './transport-lines-for-orders';
import InfoText from 'components/shared/info-text.component';
import { getLabClientByIdTemplate } from 'apollo/templates/client-directory/query.graphql';
import { LabClient } from 'models/client-directory';
import Loader from 'components/shared/loader/loader.component';

type TransportActionsProps = { order: Order };

const TransportActions: FC<TransportActionsProps> = ({ order }: TransportActionsProps) => {
    const { t: translate } = useTranslation(['orders']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [transportLinesForOrder, setTransportLinesForOrder] = useState<TransportLine[]>([]);

    const [childClientSettings, setChildClientSettings] = useState<LabClient>({} as LabClient);
    const [pickUpAndDeliveryCheck, setPickUpAndDeliveryCheck] = useState<{
        hasPickup: boolean;
        hasDelivery: boolean;
    }>({
        hasPickup: false,
        hasDelivery: false,
    });

    const { loading: labClientByIdLoading } = useQuery(getLabClientByIdTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            customerId: order?.deliveryDetails?.destination?._id,
        },
        onCompleted: ({ labClientById: childClientSettings }: { labClientById: LabClient }) => {
            setChildClientSettings(childClientSettings);
            setPickUpAndDeliveryCheck({
                hasPickup:
                    order.deliveryDetails.isPickUpAtClinic &&
                    childClientSettings?.transportProvider?.channel === TransportChannel.DENTHUB_TRANSPORT,
                hasDelivery: !!childClientSettings?.transportProvider,
            });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [getTransportLinesForOrder] = useLazyQuery(transportLinesForOrderTemplate, {
        fetchPolicy: 'network-only',
        onError: (error: ApolloError) => {
            showBoundary(error);
        },
        onCompleted: ({ transportLinesForOrder }) => {
            setTransportLinesForOrder(transportLinesForOrder || []);
        },
    });

    // The details page hands down an empty order while a version loads (DD-3847): the query
    // takes a non-null id, so it waits for one.
    useEffect(() => {
        if (order?.orderId) {
            getTransportLinesForOrder({
                variables: {
                    orderId: order.orderId,
                },
            });
        }
    }, [order?.orderId]);

    if (labClientByIdLoading) {
        return <Loader />;
    }

    return (
        <Fragment>
            {pickUpAndDeliveryCheck.hasPickup ? (
                <Box>
                    <PickUpActions order={order} />
                </Box>
            ) : undefined}

            {pickUpAndDeliveryCheck.hasPickup && pickUpAndDeliveryCheck.hasDelivery ? (
                <Box my={1}>
                    <Divider />
                </Box>
            ) : undefined}

            {pickUpAndDeliveryCheck.hasDelivery ? (
                <Box>
                    <DeliveryActions order={order} transportProvider={childClientSettings?.transportProvider} />
                </Box>
            ) : undefined}

            {!pickUpAndDeliveryCheck.hasPickup && !pickUpAndDeliveryCheck.hasDelivery ? (
                <InfoText text={translate('orders:NOT_TRANSPORT_ACTION')} />
            ) : undefined}

            {transportLinesForOrder?.length ? (
                <Fragment>
                    <Box mt={1}>
                        <Divider />
                    </Box>
                    <TransportLinesForOrder transportLinesForOrder={transportLinesForOrder} />
                </Fragment>
            ) : undefined}
        </Fragment>
    );
};

export default TransportActions;
